import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Grid,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Add as AddIcon,
  Home as HomeIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';
import type { Location } from '../../types/workflow.types';
import { geocodeAddress } from '@/utils/geocoding';

interface LocationSelectorProps {
  customerId?: string | null;
  value?: string | null;
  onChange: (locationId: string | null) => void;
  onLocationCreated?: (location: Location) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  allowCreate?: boolean;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  customerId,
  value,
  onChange,
  onLocationCreated,
  label = 'Location',
  required = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  disabled = false,
  allowCreate = true,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newLocation, setNewLocation] = useState<Partial<Location>>({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    isService: true,
  });

  // Load locations when customer changes
  useEffect(() => {
    if (customerId) {
      loadLocations();
    } else {
      setLocations([]);
      onChange(null);
    }
  }, [customerId]);

  const loadLocations = async () => {
    if (!customerId) return;
    
    setLoading(true);
    try {
      const response = await dataProvider.getList('locations', {
        filter: { customerId },
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'isPrimary', order: 'DESC' },
      });
      setLocations(response.data);
      
      // Auto-select primary location if no value is set
      if (!value && response.data.length > 0) {
        const primaryLocation = response.data.find((loc: Location) => loc.isPrimary);
        if (primaryLocation) {
          onChange(primaryLocation.id);
        }
      }
    } catch (error) {
      console.error('Failed to load locations:', error);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLocation = async () => {
    if (!customerId) {
      notify('Please select a customer first', { type: 'warning' });
      return;
    }

    if (!newLocation.addressLine1 || !newLocation.city || !newLocation.state || !newLocation.postalCode) {
      notify('Please fill in all required address fields', { type: 'warning' });
      return;
    }

    try {
      // Geocode address if coordinates are not provided
      let locationData: any = {
        ...newLocation,
        customerId,
        name: newLocation.name || `${newLocation.addressLine1}, ${newLocation.city}`,
        isPrimary: locations.length === 0, // First location is primary
      };

      // Geocode the address
      const geocodeResult = await geocodeAddress({
        streetLine1: locationData.addressLine1,
        streetLine2: locationData.addressLine2,
        city: locationData.city,
        state: locationData.state,
        postalCode: locationData.postalCode,
      });

      if (geocodeResult) {
        locationData.latitude = geocodeResult.latitude;
        locationData.longitude = geocodeResult.longitude;
      }

      const response = await dataProvider.create('locations', { data: locationData });
      
      notify('Location created successfully', { type: 'success' });
      setLocations([...locations, response.data]);
      onChange(response.data.id);
      setCreateDialogOpen(false);
      resetNewLocation();
      
      if (onLocationCreated) {
        onLocationCreated(response.data);
      }
    } catch (error) {
      notify('Failed to create location', { type: 'error' });
      console.error('Failed to create location:', error);
    }
  };

  const resetNewLocation = () => {
    setNewLocation({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      isService: true,
    });
  };

  const selectedLocation = locations.find(loc => loc.id === value);

  const getLocationLabel = (location: Location) => {
    const parts = [location.name || location.addressLine1];
    if (location.city && location.state) {
      parts.push(`${location.city}, ${location.state}`);
    }
    return parts.join(' - ');
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Autocomplete
          value={selectedLocation || null}
          onChange={(_, newValue) => onChange(newValue?.id || null)}
          options={locations}
          loading={loading}
          disabled={disabled || !customerId}
          fullWidth={fullWidth}
          size={size}
          getOptionLabel={getLocationLabel}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          renderOption={(props, option) => (
            <Box component="li" {...props}>
              <LocationIcon sx={{ mr: 2, color: 'action.active' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {option.name || option.addressLine1}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.addressLine1}
                  {option.addressLine2 && `, ${option.addressLine2}`}
                  <br />
                  {option.city}, {option.state} {option.postalCode}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {option.isPrimary && (
                    <Chip label="Primary" size="small" color="primary" icon={<HomeIcon />} />
                  )}
                  {option.isService && (
                    <Chip label="Service" size="small" variant="outlined" />
                  )}
                  {option.isBilling && (
                    <Chip label="Billing" size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              required={required}
              error={error}
              helperText={
                !customerId 
                  ? 'Select a customer first' 
                  : locations.length === 0 
                  ? 'No locations found for this customer' 
                  : helperText
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <LocationIcon sx={{ mr: 1, color: 'action.active' }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {allowCreate && customerId && (
          <IconButton
            onClick={() => setCreateDialogOpen(true)}
            disabled={disabled}
            sx={{ mt: size === 'small' ? 0.5 : 1 }}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      {/* Create Location Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Location</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Location Name"
              fullWidth
              value={newLocation.name}
              onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
              placeholder="e.g., Main Office, Warehouse #2"
              helperText="Optional - defaults to address if not provided"
            />
            
            <TextField
              label="Address Line 1"
              fullWidth
              required
              value={newLocation.addressLine1}
              onChange={(e) => setNewLocation({ ...newLocation, addressLine1: e.target.value })}
            />
            
            <TextField
              label="Address Line 2"
              fullWidth
              value={newLocation.addressLine2}
              onChange={(e) => setNewLocation({ ...newLocation, addressLine2: e.target.value })}
              placeholder="Apt, Suite, Floor, etc."
            />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  fullWidth
                  required
                  value={newLocation.city}
                  onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="State"
                  fullWidth
                  required
                  value={newLocation.state}
                  onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="ZIP Code"
                  fullWidth
                  required
                  value={newLocation.postalCode}
                  onChange={(e) => setNewLocation({ ...newLocation, postalCode: e.target.value })}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={newLocation.isService ? 'contained' : 'outlined'}
                onClick={() => setNewLocation({ ...newLocation, isService: !newLocation.isService })}
                size="small"
              >
                Service Location
              </Button>
              <Button
                variant={newLocation.isBilling ? 'contained' : 'outlined'}
                onClick={() => setNewLocation({ ...newLocation, isBilling: !newLocation.isBilling })}
                size="small"
              >
                Billing Location
              </Button>
            </Box>
            
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={2}
              value={newLocation.notes}
              onChange={(e) => setNewLocation({ ...newLocation, notes: e.target.value })}
              placeholder="Gate code, special instructions, etc."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetNewLocation();
          }}>
            Cancel
          </Button>
          <Button onClick={handleCreateLocation} variant="contained">
            Create Location
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};